import {
  Clock20Px as ClockIcon,
  ModeGoodsIn20Px as ModeGoodsInIcon,
  ModeGoodsOut20Px as ModeGoodsOutIcon,
  Error20Px as ErrorIcon,
  Service20Px as ServiceIcon,
  Hold20Px as HandIcon,
  Diagnose20Px as DiagnoseIcon
} from "@locaisolutions/icons/";
import type { TypographyProps } from "@mui/material/Typography";
import { andonColors, mainColours } from "@qubit/autoparts";

import { SystemMode, WorkstationDto } from "~/types/api";

import { hasEventType, isAutostoreEvent } from "./andon.logic";

import {
  AndonStatus,
  AndonColorState,
  AltTextKey,
  AndonEventWithMeta
} from "./andon.types";
const newAndonColors = {
  handRaisedYellow: "#ffc043",
  portFaultAmber: "#E11B22",
  gridDownPurple: andonColors.gridDownPurple,
  openGreen: "#15844B",
  workstationActiveGrey: "grey.600"
};

const andonColorStates: Record<string, AndonColorState> = {
  handRaisedYellow: {
    backgroundColor: newAndonColors.handRaisedYellow,
    borderColor: "#BA8A2C",
    textColor: "black",
    icon: HandIcon,
    altTextKey: "hand raised"
  },
  portFaultAmber: {
    backgroundColor: newAndonColors.portFaultAmber,
    borderColor: "#E11B22",
    textColor: "white",
    icon: ErrorIcon,
    altTextKey: "port fault"
  },
  gridDownPurple: {
    backgroundColor: newAndonColors.gridDownPurple,
    borderColor: "#6969ff",
    textColor: "black",
    icon: ErrorIcon,
    altTextKey: "grid error"
  },
  openGreen: {
    backgroundColor: newAndonColors.openGreen,
    borderColor: "#189A57",
    textColor: "white",
    icon: null
  },
  workstationActiveGrey: {
    backgroundColor: newAndonColors.workstationActiveGrey,
    borderColor: "#D6D3D3",
    textColor: "white",
    icon: null
  },
  default: {
    backgroundColor: "gray.light",
    borderColor: "#d6d3d3",
    textColor: "black",
    icon: null,
    altTextKey: "inactive"
  }
};

export const figureAndonColorFromStatus = ({
  handRaised,
  portOpen,
  portStatus,
  workstationStatus,
  workstationOpen,
  gridSystemMode,
  workstationActive
}: {
  handRaised?: boolean;
  gridSystemMode?: SystemMode;
  workstationStatus?: AndonStatus;
  portStatus?: AndonStatus;
  portOpen?: boolean;
  workstationOpen?: boolean;
  workstationActive?: boolean;
  defaultColor?: string;
}): {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  icon: React.ElementType | null;
  altTextKey?: AltTextKey;
} => {
  if (handRaised) return andonColorStates.handRaisedYellow;
  // ALT = ALERT ; DSTP = STOPPING
  if (gridSystemMode && ["ALT", "DSTP", "SVC", "STP"].includes(gridSystemMode))
    return andonColorStates.gridDownPurple;
  if (workstationStatus === "bad") return andonColorStates.portFaultAmber;
  if (portStatus === "bad") return andonColorStates.portFaultAmber;
  if (portOpen) return andonColorStates.openGreen;
  if (workstationOpen) return andonColorStates.openGreen;
  if (workstationActive) return andonColorStates.workstationActiveGrey;

  return andonColorStates.default;
};

export const mapEventColorAndLetters = (event: AndonEventWithMeta) => {
  let colorToReturn = "gray.light";
  let lettersToReturn = "";

  const { event: payload, hub } = event;

  // LEGACY eventType events
  if (hasEventType(payload)) {
    switch (payload.eventType) {
      case "Selected":
        colorToReturn = mainColours.label["1"];
        lettersToReturn = "PS";
        break;
      case "LabelPlaced":
        colorToReturn = mainColours.label["9"];
        lettersToReturn = "LP";
        break;
      case "StateChange":
        colorToReturn = mainColours.label["5"];
        lettersToReturn = "PTL";
        break;
      case "Position Confirmed":
        colorToReturn = mainColours.label["6"];
        lettersToReturn = "PLA";
        break;
      case "Completed":
        colorToReturn = mainColours.label["10"];
        lettersToReturn = "C";
        break;
    }
  }

  // AUTOSTORE events
  if (isAutostoreEvent(payload)) {
    switch (payload.case) {
      case "PortMode":
        switch (payload.event.portMode) {
          case "Open":
            colorToReturn = "white";
            lettersToReturn = "PO";
            break;
          case "Closed":
            colorToReturn = "#404040";
            lettersToReturn = "PC";
            break;
          case "MultiPortPuppet":
            colorToReturn = "#00000017";
            lettersToReturn = "PM";
            break;
        }
        break;
      case "PortError":
        colorToReturn = payload.event.hasError
          ? andonColors.safetyStopRed
          : andonColors.openGreen;
        lettersToReturn = payload.event.hasError ? "PE" : "POK";
        break;
      case "SystemModeChanged":
        colorToReturn = ["ALT", "DSTP", "STP", "SVC"].includes(
          payload.event.systemMode
        )
          ? andonColors.safetyStopRed
          : andonColors.openGreen;
        lettersToReturn = ["ALT", "DSTP", "STP", "SVC"].includes(
          payload.event.systemMode
        )
          ? "SMNOK"
          : "SMOK";
        break;
      case "HandToggled":
        colorToReturn = andonColors.handRaisedYellow;
        lettersToReturn = payload.event.raiseHand ? "HU" : "HD";
        break;
      case "BinRequested":
        colorToReturn = mainColours.label["2"];
        lettersToReturn = "PLR";
        break;
      case "PTLMessageRegistered":
        colorToReturn = mainColours.label["3"];
        lettersToReturn = "PLR";
        break;
      case "PTLMessageActivated":
        colorToReturn = mainColours.label["6"];
        lettersToReturn = "PLA";
        break;
      case "BinModeChange":
        switch (payload.event.binMode) {
          case "P":
            colorToReturn = mainColours.label["4"];
            lettersToReturn = "PLR";
            break;
          case "O":
            colorToReturn = mainColours.label["7"];
            lettersToReturn = "BO";
            break;
          case "C":
            colorToReturn = mainColours.label["8"];
            lettersToReturn = "BC";
            break;
        }
        break;
    }
  }

  // NEW HUB-based signalR events
  if (
    typeof hub === "string" &&
    typeof payload === "object" &&
    "eventType" in payload &&
    typeof payload.eventType === "string"
  ) {
    switch (hub) {
      case "pick":
        switch (payload.eventType) {
          case "PickCreated":
            colorToReturn = mainColours.label["1"];
            lettersToReturn = "PK";
            break;
          case "PickCompleted":
            colorToReturn = mainColours.label["10"];
            lettersToReturn = "PC";
            break;
        }
        break;
      case "tote":
        switch (payload.eventType) {
          case "On Workstation":
            colorToReturn = mainColours.label["5"];
            lettersToReturn = "TW";
            break;
          case "In Transit":
            colorToReturn = mainColours.label["3"];
            lettersToReturn = "TT";
            break;
        }
        break;
    }
  }

  return {
    color: colorToReturn,
    letters: lettersToReturn
  };
};

export function getWorkstationModeIcon(
  mode: WorkstationDto["mode"]
): React.ElementType | null {
  switch (mode) {
    case "Induction":
      return ModeGoodsInIcon;
    case "Picking":
      return ModeGoodsOutIcon;
    case "Maintenance":
      return ServiceIcon;
    case "Problem Solving":
    case "Cycle Count":
      return DiagnoseIcon;
    case "Standby":
      return ClockIcon;
    default:
      return null;
  }
}

const variantScale: TypographyProps["variant"][] = [
  "body2",
  "body1",
  "subtitle2",
  "subtitle1",
  "h6",
  "h5",
  "h4",
  "h3",
  "h2",
  "h1"
];

/**
 * Returns a variant that is `delta` steps away from the given one.
 * Positive goes larger (bolder), negative goes smaller.
 */
export function getVariantWithDelta(
  current: TypographyProps["variant"],
  delta: number
): TypographyProps["variant"] {
  const index = variantScale.indexOf(current);
  if (index === -1) return current;
  const nextIndex = Math.max(
    0,
    Math.min(variantScale.length - 1, index + delta)
  );
  return variantScale[nextIndex];
}

export function chooseVariantFromCell(
  widthPx: number,
  heightPx: number
): TypographyProps["variant"] {
  if (widthPx >= 600 || heightPx >= 400) return "h3";
  if (widthPx >= 400 || heightPx >= 300) return "h4";
  if (widthPx >= 330 || heightPx >= 160) return "h6";
  if (widthPx >= 220 || heightPx >= 90) return "body1";
  return "body2";
}
